import { useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Check, Map as MapIcon, Pencil, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import type { InternalLayer } from 'hooks/internalLayer/types';
import { useListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import type { ListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/types';
import {
  BOOM_RESOURCES,
  type CreateIcsIncidentLocationBody,
  type IcsIncidentLocation,
  type LocationPolygonProperties,
} from 'hooks/icsLocation/icsIncidentLocation.types';
import { useUscgMapContext } from './UscgMapContext';
import {
  type UscgSymbol,
  isFillSymbol,
  isSimpleLineSymbol,
  isUscgSymbol,
  symbolToGeometryTypeMap,
  usesUserColor,
} from './utils/uscgSymbology';
import { getLatLongFromGeometry } from './utils/getLatLongFromGeometry';
import { usePrimaryPoint } from './utils/usePrimaryPoint';
import { v4 as uuidv4 } from 'uuid';
import FormSymbologySelect from './FormSymbologySelect';
import { AddressLocationSearch } from './AddressLocationSearch';
import {
  collectCustomFields,
  readLocationCustomFields,
  type MapFeatureDetails,
} from './utils/locationPopup';

const CENTROID_TYPES: UscgSymbol[] = ['proposedBoom', 'oilSpill'];

function formatCoordinate(value: number) {
  return value.toFixed(6).replace(/\.?0+$/, '');
}

function coordinateIsValid(value: number, axis: 'latitude' | 'longitude') {
  if (!Number.isFinite(value)) return false;
  if (axis === 'latitude') return value >= -90 && value <= 90;
  return value >= -180 && value <= 180;
}

function VertexPointRow({
  index,
  latitude,
  longitude,
  onCommit,
  onRemove,
}: {
  index: number;
  latitude: number;
  longitude: number;
  onCommit: (
    index: number,
    point: { latitude: number; longitude: number }
  ) => void;
  onRemove: (index: number) => void;
}) {
  const [latText, setLatText] = useState(formatCoordinate(latitude));
  const [lngText, setLngText] = useState(formatCoordinate(longitude));
  const latFocused = useRef(false);
  const lngFocused = useRef(false);

  useEffect(() => {
    if (!latFocused.current) setLatText(formatCoordinate(latitude));
  }, [latitude]);

  useEffect(() => {
    if (!lngFocused.current) setLngText(formatCoordinate(longitude));
  }, [longitude]);

  const commit = (nextLat: string, nextLng: string) => {
    const lat = Number(nextLat);
    const lng = Number(nextLng);
    const latOk = coordinateIsValid(lat, 'latitude');
    const lngOk = coordinateIsValid(lng, 'longitude');
    if (!latOk) setLatText(formatCoordinate(latitude));
    if (!lngOk) setLngText(formatCoordinate(longitude));
    if (!latOk || !lngOk) return;
    if (lat === latitude && lng === longitude) return;
    onCommit(index, { latitude: lat, longitude: lng });
  };

  return (
    <li className="tw:flex tw:items-center tw:gap-2">
      <span className="tw:w-4 tw:shrink-0 tw:text-xs tw:tabular-nums">
        {index + 1}
      </span>
      <Input
        aria-label={`Point ${index + 1} latitude`}
        value={latText}
        className="tw:h-8"
        onFocus={() => {
          latFocused.current = true;
        }}
        onChange={(event) => setLatText(event.target.value)}
        onBlur={() => {
          latFocused.current = false;
          commit(latText, lngText);
        }}
      />
      <Input
        aria-label={`Point ${index + 1} longitude`}
        value={lngText}
        className="tw:h-8"
        onFocus={() => {
          lngFocused.current = true;
        }}
        onChange={(event) => setLngText(event.target.value)}
        onBlur={() => {
          lngFocused.current = false;
          commit(latText, lngText);
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label={`Delete point ${index + 1}`}
        title="Delete point"
        onClick={() => onRemove(index)}
      >
        <Trash2 className="tw:size-3.5" />
      </Button>
    </li>
  );
}

type CreateFeatureFormProps = {
  internalLayer?: InternalLayer | null;
  existingLocation?: IcsIncidentLocation | null;
  onClose: () => void;
  onDiscard?: () => void;
  onSubmit: ({
    location,
    pendingGraphicId,
  }: {
    location: CreateIcsIncidentLocationBody;
    pendingGraphicId?: string;
  }) => Promise<void> | void;
};

export function CreateFeatureForm({
  internalLayer,
  existingLocation,
  onClose,
  onDiscard,
  onSubmit,
}: CreateFeatureFormProps) {
  const {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    applyTypedCoordinates,
    stopDrawing,
    symbolDrawingState,
    connectDrawingPoints,
    resetDrawing,
    getDrawnGraphic,
    describeDrawnGraphic,
    zoomToLocation,
    zoomToGraphic,
    getSelectedVersionForLayer,
  } = useUscgMapContext();

  const addObjectForm = useForm({
    defaultValues: {
      featureName: '',
      latitude: '',
      longitude: '',
      description: '',
      symbology: '',
      polygonColor: '#1e40af',
      polygonOpacity: '25',
      customFields: [] as { label: string; value: string }[],
    },
  });

  const {
    setValue,
    setFocus,
    reset,
    handleSubmit,
    getValues,
    watch,
    register,
    control,
    formState,
  } = addObjectForm;
  const [selectedType, setSelectedType] = useState<UscgSymbol | null>(null);
  const [hasSaved, setHasSaved] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const editAnchorRef = useRef<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const skipLatitudeFocusRef = useRef(false);
  const polygonColor = watch('polygonColor');
  const polygonOpacity = watch('polygonOpacity');
  const latitudeValue = watch('latitude');
  const longitudeValue = watch('longitude');
  const featureName = watch('featureName');
  const featureDescription = watch('description');
  const customFieldValues = watch('customFields');
  const {
    fields: customFieldRows,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'customFields',
  });

  const versionsQuery: ListInternalLayerVersionsQuery = {
    filter: {
      internalLayerId: internalLayer?.id,
      status: 'DRAFT',
    },
  };

  const { data: versionsData } = useListInternalLayerVersionsQuery(
    versionsQuery,
    { enabled: internalLayer != null }
  );
  const draftVersion = versionsData?.data?.[0];
  const existingLocationId = existingLocation?.id;

  useEffect(() => {
    if (!existingLocation || !isUscgSymbol(existingLocation.resourceType)) {
      return;
    }
    const coords =
      existingLocation.geometry?.type === 'Point'
        ? {
            latitude: existingLocation.geometry.coordinates[1],
            longitude: existingLocation.geometry.coordinates[0],
          }
        : getLatLongFromGeometry(existingLocation.geometry);
    const properties = (existingLocation.properties ??
      {}) as LocationPolygonProperties;
    const opacity = properties.pointOpacity ?? properties.polygonOpacity;
    reset({
      featureName: existingLocation.name ?? '',
      description: existingLocation.description ?? '',
      latitude: coords ? String(coords.latitude) : '',
      longitude: coords ? String(coords.longitude) : '',
      symbology: existingLocation.resourceType,
      polygonColor:
        properties.pointColor ?? properties.polygonColor ?? '#1e40af',
      polygonOpacity: String(
        Math.round(
          (opacity ??
            (existingLocation.resourceType === 'customPoint' ? 1 : 0.25)) * 100
        )
      ),
      customFields: readLocationCustomFields(existingLocation.properties),
    });
    setSelectedType(existingLocation.resourceType);
    // Prefill once per object. Later form edits must not be reset by a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingLocationId]);

  const isBoomSelected = selectedType === 'proposedBoom';
  const geometryKind = selectedType
    ? symbolToGeometryTypeMap[selectedType]
    : null;
  const isPolygonSelected = selectedType ? isFillSymbol(selectedType) : false;
  const showColor = selectedType ? usesUserColor(selectedType) : false;
  const isMultiVertex = geometryKind === 'line' || geometryKind === 'polygon';
  const boomHasMultiplePoints =
    isBoomSelected && symbolDrawingState.points.length >= 2;
  const locationInputsLocked =
    geometryKind === 'single' &&
    symbolDrawingState.points.length > 0 &&
    !isEditingLocation;

  const primaryPoint = usePrimaryPoint({
    selectedType,
    centroidTypes: CENTROID_TYPES,
  });
  const placedLatitude =
    geometryKind === 'single' ? primaryPoint?.latitude : undefined;
  const placedLongitude =
    geometryKind === 'single' ? primaryPoint?.longitude : undefined;

  useEffect(() => {
    if (
      geometryKind !== 'single' ||
      placedLatitude == null ||
      placedLongitude == null
    ) {
      return;
    }
    if (isEditingLocation) {
      const anchor = editAnchorRef.current;
      if (
        anchor &&
        anchor.latitude === placedLatitude &&
        anchor.longitude === placedLongitude
      ) {
        return;
      }
    }
    setValue('latitude', String(placedLatitude));
    setValue('longitude', String(placedLongitude));
  }, [
    geometryKind,
    placedLatitude,
    placedLongitude,
    isEditingLocation,
    setValue,
  ]);

  useEffect(() => {
    if (!isEditingLocation) setIsAddressSearchOpen(false);
  }, [isEditingLocation]);

  useEffect(() => {
    if (!isEditingLocation) return;
    if (skipLatitudeFocusRef.current) {
      skipLatitudeFocusRef.current = false;
      return;
    }
    setFocus('latitude', { shouldSelect: true });
  }, [isEditingLocation, setFocus]);

  const handleSymbologyChange = (type: string | null) => {
    if (type) {
      if (usesUserColor(type as UscgSymbol)) {
        setPolygonDrawingColor(getValues('polygonColor'));
        const opacityPercent = Number(
          type === 'customPoint' ? '100' : getValues('polygonOpacity')
        );
        if (type === 'customPoint') setValue('polygonOpacity', '100');
        const clampedPercent = Number.isNaN(opacityPercent)
          ? type === 'customPoint'
            ? 100
            : 25
          : Math.max(0, Math.min(100, opacityPercent));
        setPolygonDrawingOpacity(clampedPercent / 100);
      } else {
        setPolygonDrawingColor('#1e40af');
        setPolygonDrawingOpacity(0.25);
      }
      startDrawing(type as UscgSymbol);
    } else {
      stopDrawing();
    }
    setIsEditingLocation(false);
    setIsAddressSearchOpen(false);
    editAnchorRef.current = null;
    setValue('latitude', '');
    setValue('longitude', '');
  };

  const currentFeatureDetails = (): MapFeatureDetails => ({
    name: getValues('featureName'),
    description: getValues('description'),
    resourceType: selectedType ?? undefined,
    customFields: collectCustomFields(getValues('customFields')),
  });

  useEffect(() => {
    if (!selectedType) return;
    describeDrawnGraphic({
      name: featureName,
      description: featureDescription,
      resourceType: selectedType,
      customFields: collectCustomFields(customFieldValues),
    });
  }, [
    customFieldValues,
    describeDrawnGraphic,
    featureDescription,
    featureName,
    selectedType,
    symbolDrawingState.line,
    symbolDrawingState.points,
    symbolDrawingState.polygon,
  ]);

  const readTypedPair = () => {
    const latitude = Number(getValues('latitude'));
    const longitude = Number(getValues('longitude'));
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    if (latitude < -90 || latitude > 90) return null;
    if (longitude < -180 || longitude > 180) return null;
    return { latitude, longitude };
  };

  const placeTypedPoint = () => {
    const pair = readTypedPair();
    if (!pair || !selectedType) return;
    const existing = symbolDrawingState.points.map((point) => ({
      latitude: point.latitude,
      longitude: point.longitude,
    }));
    const next = geometryKind === 'single' ? [pair] : [...existing, pair];
    applyTypedCoordinates(next, currentFeatureDetails());
    if (geometryKind !== 'single') {
      setValue('latitude', '');
      setValue('longitude', '');
    }
  };

  const replaceTypedPoints = (
    points: { latitude: number; longitude: number }[]
  ) => {
    applyTypedCoordinates(points, currentFeatureDetails());
  };

  const updateTypedPoint = (
    index: number,
    point: { latitude: number; longitude: number }
  ) => {
    replaceTypedPoints(
      symbolDrawingState.points.map((existing, pointIndex) =>
        pointIndex === index
          ? point
          : {
              latitude: existing.latitude,
              longitude: existing.longitude,
            }
      )
    );
  };

  const removeTypedPoint = (index: number) => {
    replaceTypedPoints(
      symbolDrawingState.points
        .filter((_, pointIndex) => pointIndex !== index)
        .map((point) => ({
          latitude: point.latitude,
          longitude: point.longitude,
        }))
    );
  };

  const handleConnectPointsClick = () => {
    if (!selectedType || selectedType !== 'proposedBoom') return;
    connectDrawingPoints(selectedType);
  };

  const typedPairIsValid = () => {
    const latText = String(latitudeValue ?? '').trim();
    const lngText = String(longitudeValue ?? '').trim();
    if (!latText || !lngText) return false;
    const latitude = Number(latText);
    const longitude = Number(lngText);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
    if (latitude < -90 || latitude > 90) return false;
    if (longitude < -180 || longitude > 180) return false;
    return true;
  };

  const canZoomToLocation = typedPairIsValid();
  const pointLocationReady =
    geometryKind === 'single' &&
    (symbolDrawingState.points.length > 0 ||
      primaryPoint != null ||
      typedPairIsValid());

  const zoomToEnteredLocation = () => {
    if (geometryKind === 'single') {
      const pair = readTypedPair();
      if (pair && selectedType) {
        applyTypedCoordinates([pair], currentFeatureDetails());
      }
      const graphic = getDrawnGraphic();
      if (graphic) {
        void zoomToGraphic({ graphic, zoom: 14 });
        return;
      }
      if (pair) void zoomToLocation({ ...pair, zoom: 14 });
      return;
    }

    let graphic = getDrawnGraphic();
    if (
      !graphic &&
      selectedType &&
      symbolDrawingState.points.length >= (isPolygonSelected ? 3 : 2)
    ) {
      applyTypedCoordinates(
        symbolDrawingState.points.map((point) => ({
          latitude: point.latitude,
          longitude: point.longitude,
        })),
        currentFeatureDetails()
      );
      graphic = getDrawnGraphic();
    }

    if (graphic?.geometry) {
      void zoomToGraphic({
        graphic,
        zoom: graphic.geometry.type === 'point' ? 14 : undefined,
      });
      return;
    }

    const pair = readTypedPair();
    if (pair) void zoomToLocation({ ...pair, zoom: 14 });
  };

  const startEditingLocation = () => {
    editAnchorRef.current =
      placedLatitude != null && placedLongitude != null
        ? { latitude: placedLatitude, longitude: placedLongitude }
        : null;
    setIsEditingLocation(true);
  };

  const enterLocationEditFromField = () => {
    if (isEditingLocation) return;
    skipLatitudeFocusRef.current = true;
    startEditingLocation();
  };

  const applySearchedAddress = (location: {
    latitude: number;
    longitude: number;
  }) => {
    setValue('latitude', String(location.latitude));
    setValue('longitude', String(location.longitude));
    if (geometryKind === 'single' && selectedType) {
      applyTypedCoordinates([location], currentFeatureDetails());
      const graphic = getDrawnGraphic();
      if (graphic) {
        void zoomToGraphic({ graphic, zoom: 14 });
        return;
      }
    }
    void zoomToLocation({ ...location, zoom: 14 });
  };

  const drawLocationOnMap = () => {
    if (!selectedType) return;
    setValue('latitude', '');
    setValue('longitude', '');
    startDrawing(selectedType);
  };

  const cancelLocationEdit = () => {
    const anchor = editAnchorRef.current;
    setIsEditingLocation(false);
    if (!anchor) {
      setValue('latitude', '');
      setValue('longitude', '');
      return;
    }
    setValue('latitude', String(anchor.latitude));
    setValue('longitude', String(anchor.longitude));
    if (selectedType && geometryKind === 'single') {
      applyTypedCoordinates([anchor], currentFeatureDetails());
    }
  };

  const handleClearClick = () => {
    // Clear temporary drawing state and re-start drawing for the
    // currently selected symbology (if any).
    resetDrawing();
    setIsEditingLocation(false);
    editAnchorRef.current = null;
    setValue('latitude', '');
    setValue('longitude', '');

    if (selectedType) {
      handleSymbologyChange(selectedType);
    }
  };

  const handleSave = handleSubmit(async (data) => {
    if (!selectedType) return;

    const { points, line } = symbolDrawingState;
    const isNewLayer = internalLayer == null;
    const selectedVersion = internalLayer
      ? getSelectedVersionForLayer(internalLayer.id)
      : undefined;
    const versionId = selectedVersion?.id ?? draftVersion?.id ?? null;

    const createdAt = new Date().toISOString();
    const locationPoints = points.map((p, idx) => ({
      id: p.id,
      longitude: p.longitude,
      latitude: p.latitude,
      order: p.order ?? idx,
      createdAt,
    }));

    const customFields = collectCustomFields(data.customFields);
    const featureDetails = (resourceType: string): MapFeatureDetails => ({
      name: data.featureName,
      description: data.description,
      resourceType,
      customFields,
    });
    const propertiesWithFields = (
      properties: LocationPolygonProperties
    ): LocationPolygonProperties =>
      customFields.length ? { ...properties, customFields } : properties;

    let locationInput: CreateIcsIncidentLocationBody;
    // eslint-disable-next-line unused-imports/no-unused-vars
    let runPendingUpload = (pendingGraphicId: string) => {};
    const reuseExistingGeometry =
      Boolean(existingLocation) &&
      points.length === 0 &&
      !symbolDrawingState.polygon &&
      !line;

    if (reuseExistingGeometry && existingLocation) {
      const typedLat = Number(data.latitude);
      const typedLng = Number(data.longitude);
      const typedPoint = Number.isFinite(typedLat) && Number.isFinite(typedLng);
      const geometry =
        existingLocation.geometry?.type === 'Point' && typedPoint
          ? {
              type: 'Point' as const,
              coordinates: [typedLng, typedLat] as [number, number],
            }
          : existingLocation.geometry;
      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: selectedType,
        properties: propertiesWithFields(
          (existingLocation.properties ?? {}) as LocationPolygonProperties
        ),
        geometry,
        internalLayerVersionId:
          versionId ?? existingLocation.internalLayerVersionId,
      };
    } else if (data.symbology === 'proposedBoom') {
      const resourceType = line
        ? BOOM_RESOURCES.COMPLETED
        : BOOM_RESOURCES.PROPOSED;
      locationInput = {
        name: data.featureName,
        description: data.description,
        geometry: {
          type: 'LineString',
          coordinates: locationPoints.map((p) => [p.longitude, p.latitude]),
        },
        resourceType,
        properties: propertiesWithFields({}),
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        locationPoints.forEach((p) =>
          addPendingPoint({
            symbolKey: 'proposedBoom',
            point: p,
            pendingGraphicId,
            details: featureDetails(resourceType),
          })
        );
        if (line) {
          addPendingPolyline({
            symbolKey: 'proposedBoom',
            points: locationPoints,
            pendingGraphicId,
            details: featureDetails(resourceType),
          });
        }
      };
    } else if (data.symbology === 'oilSpill') {
      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: 'oilSpill',
        geometry: {
          type: 'LineString',
          coordinates: locationPoints.map((p) => [p.longitude, p.latitude]),
        },
        properties: propertiesWithFields({}),
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPolyline({
          symbolKey: 'oilSpill',
          points: locationPoints,
          pendingGraphicId,
          details: featureDetails('oilSpill'),
        });
      };
    } else if (selectedType && isFillSymbol(selectedType)) {
      const polygon = symbolDrawingState.polygon;
      if (!polygon) return;

      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: selectedType,
        properties: propertiesWithFields(
          selectedType === 'customPolygon'
            ? {
                polygonColor: data.polygonColor,
                polygonOpacity: Math.max(
                  0,
                  Math.min(1, Number(data.polygonOpacity || '25') / 100)
                ),
              }
            : {}
        ),
        geometry: polygon,
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPolygon({
          geometry: polygon,
          colorHex: data.polygonColor,
          opacity:
            selectedType === 'customPolygon'
              ? Math.max(
                  0,
                  Math.min(1, Number(data.polygonOpacity || '25') / 100)
                )
              : undefined,
          pendingGraphicId,
          details: featureDetails(selectedType),
        });
      };
    } else if (selectedType && isSimpleLineSymbol(selectedType)) {
      locationInput = {
        name: data.featureName,
        description: data.description,
        resourceType: selectedType,
        geometry: {
          type: 'LineString',
          coordinates: locationPoints.map((p) => [p.longitude, p.latitude]),
        },
        properties: propertiesWithFields({}),
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPolyline({
          symbolKey: selectedType,
          points: locationPoints,
          pendingGraphicId,
          details: featureDetails(selectedType),
        });
      };
    } else if (data.symbology === 'customPoint') {
      const coords = {
        longitude: Number(data.longitude),
        latitude: Number(data.latitude),
      };
      const pointOpacity = Math.max(
        0,
        Math.min(1, Number(data.polygonOpacity || '100') / 100)
      );
      locationInput = {
        name: data.featureName,
        description: data.description,
        geometry: {
          type: 'Point',
          coordinates: [coords.longitude, coords.latitude],
        },
        resourceType: 'customPoint',
        properties: propertiesWithFields({
          pointColor: data.polygonColor,
          pointOpacity,
        }),
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPoint({
          symbolKey: 'customPoint',
          point: coords,
          pendingGraphicId,
          colorHex: data.polygonColor,
          opacity: pointOpacity,
          details: featureDetails('customPoint'),
        });
      };
    } else {
      const coords = {
        longitude: Number(data.longitude),
        latitude: Number(data.latitude),
      };
      locationInput = {
        name: data.featureName,
        description: data.description,
        geometry: {
          type: 'Point',
          coordinates: [coords.longitude, coords.latitude],
        },
        resourceType: selectedType,
        properties: propertiesWithFields({}),
        internalLayerVersionId: versionId,
      };

      runPendingUpload = (pendingGraphicId: string) => {
        addPendingPoint({
          symbolKey: selectedType,
          point: coords,
          pendingGraphicId,
          details: featureDetails(selectedType),
        });
      };
    }

    let pendingGraphicId: string | undefined;
    if (isNewLayer) {
      pendingGraphicId = uuidv4();
      runPendingUpload(pendingGraphicId);
    }

    await onSubmit({
      location: locationInput,
      pendingGraphicId,
    });

    setHasSaved(true);
    stopDrawing();
    setSelectedType(null);
    setIsEditingLocation(false);
    editAnchorRef.current = null;
    reset();
    onClose();
  });

  const handleCancel = () => {
    // Cancel should discard any temporary graphics and form state.
    stopDrawing();
    setSelectedType(null);
    setIsEditingLocation(false);
    editAnchorRef.current = null;
    reset();

    // If layer hasn't been created yet, discard the last committed pending object.
    if (hasSaved) {
      setHasSaved(false);
      onDiscard?.();
    }

    onClose();
  };

  return (
    <FormProvider {...addObjectForm}>
      <div className="dark tw:mt-2 tw:flex tw:flex-col tw:gap-3 tw:rounded tw:border tw:border-[var(--border-primary)] tw:bg-background tw:p-4 tw:text-foreground">
        <Field>
          <FieldLabel htmlFor="create-feature-name">
            <span>
              Name
              <span className="tw:text-destructive" aria-hidden="true">
                {' '}
                *
              </span>
            </span>
          </FieldLabel>
          <Input
            id="create-feature-name"
            aria-invalid={!!formState.errors.featureName}
            {...register('featureName', {
              required: { value: true, message: 'Required' },
            })}
          />
          <FieldError errors={[formState.errors.featureName]} />
        </Field>
        <FormSymbologySelect
          variant="shadcn"
          required
          excludeOptions={['completedBoom']}
          includeCustomPolygon
          onSymbologyChange={(newValue) => {
            setSelectedType(newValue);
            handleSymbologyChange(newValue);
          }}
        />
        <p className="tw:mb-0 tw:text-xs tw:text-muted-foreground">
          {isPolygonSelected
            ? 'Click the map to draw a polygon, or enter at least three points.'
            : geometryKind === 'line'
              ? 'Click the map to draw a line, or enter at least two points.'
              : 'Click the map to add a point, or enter a latitude and longitude.'}
        </p>
        {showColor && (
          <div className="tw:grid tw:gap-3 tw:sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="polygonColor">
                {selectedType === 'customPoint'
                  ? 'Point color'
                  : 'Polygon color'}
              </FieldLabel>
              {/* No shadcn colour input exists, so the native one is themed in place. */}
              <div className="tw:flex tw:items-center tw:gap-2">
                <input
                  id="polygonColor"
                  type="color"
                  title="Choose polygon color"
                  className="tw:h-9 tw:w-12 tw:shrink-0 tw:cursor-pointer tw:rounded-md tw:border tw:border-input tw:bg-transparent tw:p-1"
                  {...register('polygonColor', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      setPolygonDrawingColor(e.target.value);
                    },
                  })}
                />
                <span className="tw:text-xs tw:text-muted-foreground tw:tabular-nums">
                  {String(polygonColor ?? '').toUpperCase()}
                </span>
              </div>
            </Field>

            <Field>
              <FieldLabel htmlFor="polygonOpacity">Transparency</FieldLabel>
              <div className="tw:flex tw:items-center tw:gap-2">
                <input
                  id="polygonOpacity"
                  type="range"
                  min="0"
                  max="100"
                  title="Adjust polygon transparency"
                  className="tw:h-9 tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:accent-primary"
                  {...register('polygonOpacity', {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      const percent = Number(e.target.value);
                      const clamped = Number.isNaN(percent)
                        ? 25
                        : Math.max(0, Math.min(100, percent));
                      setPolygonDrawingOpacity(clamped / 100);
                    },
                  })}
                />
                <span className="tw:w-10 tw:shrink-0 tw:text-xs tw:text-muted-foreground tw:tabular-nums">
                  {`${Math.max(0, Math.min(100, Number(polygonOpacity || '25')))}%`}
                </span>
              </div>
            </Field>
          </div>
        )}
        {selectedType && geometryKind !== 'single' ? (
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            {isBoomSelected && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!boomHasMultiplePoints}
                onClick={handleConnectPointsClick}
              >
                Connect points
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearClick}
            >
              {isPolygonSelected ? 'Redraw polygon' : 'Redraw line'}
            </Button>
          </div>
        ) : null}
        <div className="tw:flex tw:items-end tw:gap-2">
          <div className="tw:grid tw:min-w-0 tw:flex-1 tw:gap-3 tw:sm:grid-cols-2">
            <Field>
              <FieldLabel htmlFor="create-feature-latitude">
                <span>
                  Latitude
                  <span className="tw:text-destructive" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </span>
              </FieldLabel>
              <Input
                id="create-feature-latitude"
                aria-required
                {...register('latitude')}
                readOnly={locationInputsLocked}
                onFocus={enterLocationEditFromField}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="create-feature-longitude">
                <span>
                  Longitude
                  <span className="tw:text-destructive" aria-hidden="true">
                    {' '}
                    *
                  </span>
                </span>
              </FieldLabel>
              <Input
                id="create-feature-longitude"
                aria-required
                {...register('longitude')}
                readOnly={locationInputsLocked}
                onFocus={enterLocationEditFromField}
              />
            </Field>
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="tw:h-9 tw:w-9"
              aria-label="Zoom to location"
              title="Zoom to location"
              disabled={!canZoomToLocation}
              onClick={zoomToEnteredLocation}
            >
              <MapIcon />
            </Button>
            {isEditingLocation ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="tw:h-9 tw:w-9"
                  aria-label="Save location"
                  title="Save location"
                  disabled={
                    !selectedType ||
                    (!existingLocation &&
                      !(
                        pointLocationReady ||
                        (geometryKind === 'line' &&
                          symbolDrawingState.points.length >= 2) ||
                        (isPolygonSelected &&
                          Boolean(symbolDrawingState.polygon))
                      ))
                  }
                  onClick={() => void handleSave()}
                >
                  <Check />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="tw:h-9 tw:w-9"
                  aria-label="Cancel location edit"
                  title="Cancel location edit"
                  onClick={cancelLocationEdit}
                >
                  <X />
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="tw:h-9 tw:w-9"
                aria-label="Edit location"
                title="Edit location"
                disabled={!selectedType}
                onClick={startEditingLocation}
              >
                <Pencil />
              </Button>
            )}
          </div>
        </div>
        {isEditingLocation ? (
          <div className="tw:flex tw:flex-col tw:gap-2">
            <div className="tw:flex tw:flex-wrap tw:gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={isAddressSearchOpen}
                onClick={() => setIsAddressSearchOpen((open) => !open)}
              >
                <Search />
                Search Location
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={drawLocationOnMap}
              >
                Draw Location on Map
                <MapIcon />
              </Button>
            </div>
            {isAddressSearchOpen ? (
              <AddressLocationSearch onSelect={applySearchedAddress} />
            ) : null}
          </div>
        ) : null}
        {selectedType &&
        (isMultiVertex ||
          !(
            geometryKind === 'single' &&
            (isEditingLocation || symbolDrawingState.points.length > 0)
          )) ? (
          <div className="tw:flex tw:flex-col tw:gap-2">
            {geometryKind === 'single' &&
            (isEditingLocation ||
              symbolDrawingState.points.length > 0) ? null : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="tw:self-start"
                onClick={placeTypedPoint}
              >
                {geometryKind === 'single' ? 'Place point' : 'Add point'}
              </Button>
            )}
            {isMultiVertex && symbolDrawingState.points.length > 0 ? (
              <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
                {symbolDrawingState.points.map((point, index) => (
                  <VertexPointRow
                    key={index}
                    index={index}
                    latitude={point.latitude}
                    longitude={point.longitude}
                    onCommit={updateTypedPoint}
                    onRemove={removeTypedPoint}
                  />
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
        <Field>
          <FieldLabel htmlFor="create-feature-description">
            Description
          </FieldLabel>
          <Input id="create-feature-description" {...register('description')} />
        </Field>
        <div className="tw:flex tw:flex-col tw:gap-2">
          {customFieldRows.map((field, index) => (
            <div key={field.id} className="tw:flex tw:items-end tw:gap-2">
              <Field className="tw:min-w-0 tw:flex-1">
                <FieldLabel htmlFor={`create-feature-field-label-${field.id}`}>
                  Field
                </FieldLabel>
                <Input
                  id={`create-feature-field-label-${field.id}`}
                  placeholder="Name"
                  {...register(`customFields.${index}.label`)}
                />
              </Field>
              <Field className="tw:min-w-0 tw:flex-1">
                <FieldLabel htmlFor={`create-feature-field-value-${field.id}`}>
                  Value
                </FieldLabel>
                <Input
                  id={`create-feature-field-value-${field.id}`}
                  placeholder="Value"
                  {...register(`customFields.${index}.value`)}
                />
              </Field>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Remove field"
                title="Remove field"
                onClick={() => remove(index)}
              >
                <X />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tw:self-start"
            onClick={() => append({ label: '', value: '' })}
          >
            + Add Field
          </Button>
        </div>
        <div className="tw:flex tw:gap-2">
          <Button
            type="button"
            size="sm"
            disabled={
              !selectedType ||
              (!existingLocation &&
                (isPolygonSelected
                  ? !symbolDrawingState.polygon
                  : geometryKind === 'line'
                    ? symbolDrawingState.points.length < 2
                    : symbolDrawingState.points.length < 1 && !primaryPoint))
            }
            onClick={handleSave}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
